<template>
  <div>
    <button class="mobile-menu-toggle" :class="{ 'desktop-visible': isCollapsed && !isOverlayMode() }"
      @click="toggleMobileMenu">
      <font-awesome-icon :icon="isMobileMenuOpen ? 'times' : 'bars'" class="fixed-icon-size" />
    </button>
    <div :class="[
      'menu-container',
      {
        'dark-mode': isDarkMode,
        collapsed: isCollapsed && !isMobileMenuOpen,
        'desktop-hidden': isCollapsed && !isMobileMenuOpen && !isOverlayMode(),
        'mobile-open': isMobileMenuOpen,
      },
    ]" @mouseleave="handleMouseLeave" ref="menuContainer" tabindex="0" @blur="handleMenuBlur">
      <div class="logo-section">
        <div class="logo-wrapper">
          <img :src="logoSrc" alt="Logo" class="logo" :class="{ 'logo-collapsed': isCollapsed }"
            @click="toggleCollapse" />
        </div>
      </div>
      <div class="menu-section">
        <ul class="menu-list">
          <li v-for="menuItem in (dynamicMenuItems || [])" :key="menuItem.title" class="menu-item"
            @mouseover="handleMouseOver">
            <div @click="
              menuItem.subMenu && menuItem.subMenu.length > 0 && toggleDropdown(menuItem.title)
              " :class="{ clickable: menuItem.subMenu && menuItem.subMenu.length > 0 }">
              <font-awesome-icon :icon="menuItem.icon" class="fixed-icon-size" />
              <span v-if="!isCollapsed" class="menu-item-text">{{
                menuItem.title
              }}</span>
              <font-awesome-icon v-if="menuItem.subMenu && menuItem.subMenu.length > 0 && !isCollapsed"
                :icon="openDropdown === menuItem.title ? 'angle-left' : 'angle-right'" class="dropdown-icon" />
            </div>
            <div class="sub-menu-container"
              v-show="!menuItem.subMenu || menuItem.subMenu.length === 0 || openDropdown === menuItem.title">
              <ul class="sub-menu-list">
                <li v-for="subMenuItem in getFilteredSubMenu(menuItem)" :key="subMenuItem.title" class="sub-menu-item"
                  @click="navigateTo(subMenuItem.route)">
                  <font-awesome-icon :icon="subMenuItem.icon" class="fixed-icon-size" />
                  <span v-if="!isCollapsed" class="submenu-item-text">{{
                    subMenuItem.title
                  }}</span>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
      <div class="user-section" @mouseover="handleMouseOver">
        <div class="user-text">
          <img :src="require('../assets/layout/user.webp')" alt="User Photo" class="user-photo" />
          <span class="user-name" v-if="!isCollapsed" :title="userName">{{
            userName.length > 20 ? userName.substring(0, 20) + "..." : userName
          }}</span>
        </div>
        <div class="user-buttons">
          <button @click="CerrarLogin" class="logout-button">
            <span class="user-name" v-if="!isCollapsed">Cerrar Sesion</span>
            <font-awesome-icon icon="sign-out-alt" class="fixed-icon-size" />
          </button>

          <div class="switchdiv">
            <span class="user-name" v-if="!isCollapsed"> Modo Oscuro </span>
            <label class="switch">
              <input type="checkbox" @change="toggleDarkMode" :checked="isDarkMode" />
              <span class="slider round"></span>
            </label>
          </div>
        </div>
      </div>
    </div>
    <div class="menu-overlay" v-if="isMobileMenuOpen" @click="closeMobileMenu"></div>
  </div>
</template>

<script>
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import MenuService from '../service/MenuService';

import {
  faTasks,
  faClipboardList,
  faUserCheck,
  faUsers,
  faUserPlus,
  faFileInvoiceDollar,
  faStore,
  faStoreAlt,
  faSun,
  faMoon,
  faSignOutAlt,
  faAngleRight,
  faAngleLeft,
  faRoute,
  faBars,
  faTimes,
  faTruck,
  faShieldAlt,
  faDesktop,
  faList,
  faUnlock,
  faStar,
  faKey,
  faFolderOpen,
} from "@fortawesome/free-solid-svg-icons";

library.add(
  faTasks,
  faClipboardList,
  faUserCheck,
  faUsers,
  faUserPlus,
  faFileInvoiceDollar,
  faStore,
  faStoreAlt,
  faSun,
  faMoon,
  faSignOutAlt,
  faAngleRight,
  faAngleLeft,
  faRoute,
  faBars,
  faTimes,
  faTruck,
  faShieldAlt,
  faDesktop,
  faList,
  faUnlock,
  faStar,
  faKey,
  faFolderOpen
);

export default {
  name: "MenuDynamic",
  components: {
    FontAwesomeIcon,
  },
  props: {
    userData: {
      type: Object,
      required: true
    },
  },
  data() {
    return {
      dynamicMenuItems: [],
      loading: false,
      error: null,
      menuService: new MenuService(),
      isCollapsed: false,
      isDarkMode: false,
      isMobileMenuOpen: false,
      isHovering: false,
      openDropdown: null,
      userName: localStorage.getItem("nombreUsuario") || "Usuario",
      clickTimeout: null,
      clickHandlerActive: false,
      resizeHandler: null,
    };
  },
  computed: {
    userName() {
      return this.userData && this.userData.data && this.userData.data.nombre
        ? this.userData.data.nombre
        : localStorage.getItem("nombre") || "Usuario";
    },
    logoSrc() {
      if (this.isCollapsed) {
        return require("../assets/layout/logo-collapsed.webp");
      } else {
        return this.isDarkMode
          ? require("../assets/layout/betaD.webp")
          : require("../assets/layout/betaL.webp");
      }
    },
  },
  async created() {
    await this.loadDynamicMenu();
    this.loadUserPreferences();
    this.syncMenuMode();
  },
  methods: {
    isOverlayMode() {
      return window.innerWidth <= 1100;
    },

    syncMenuMode() {
      if (this.isOverlayMode()) {
        this.isCollapsed = false;
      } else {
        this.isMobileMenuOpen = false;
        sessionStorage.setItem("mobileMenuOpen", false);
      }
    },

    async loadDynamicMenu() {
      this.loading = true;
      this.error = null;

      try {
        const usuario = localStorage.getItem('usuario');
        if (!usuario) throw new Error('Usuario no identificado');

        // Obtener menú estándar y accesos especiales en paralelo
        const [menuData, especiales] = await Promise.all([
          this.menuService.getMenuByUsuario(usuario),
          this.menuService.catalogService.getAllAccesosEspeciales(usuario)
        ]);

        // 1. Convertir menú estándar (Roles)
        let finalMenuItems = this.menuService.convertToMenuFormat(menuData);

        // 2. Integrar Accesos Especiales (Modo Unión/Suma)
        if (especiales && especiales.length > 0) {
          especiales.forEach(esp => {
            const routeName = esp.subaccesoRoute || esp.ruta;
            
            // Validar ruta física en el router
            if (!routeName || !this.$router.hasRoute(routeName)) return;

            const categoryTitle = esp.sistemaNombre || esp.accesoNombre || 'Accesos Especiales';
            
            // Buscar si ya existe la categoría (Acceso Padre) en el menú combinado
            let parent = finalMenuItems.find(m => m.title.toLowerCase() === categoryTitle.toLowerCase());
            
            if (!parent) {
              parent = {
                title: categoryTitle,
                icon: esp.accesoIcono || esp.sistemaIcono || 'folder-open',
                orden: 99,
                subMenu: []
              };
              finalMenuItems.push(parent);
            }

            // Agregar subacceso si no existe ya en esa categoría (evitar duplicados)
            const subExists = parent.subMenu.find(sub => sub.route === routeName);
            if (!subExists) {
              parent.subMenu.push({
                title: esp.subaccesoNombre || 'Pantalla',
                icon: esp.subaccesoIcono || 'star',
                route: routeName,
                orden: esp.subaccesoOrden || 1
              });
            }
          });
        }

        this.dynamicMenuItems = finalMenuItems;

        // Ordenar los elementos del menú por orden
        this.dynamicMenuItems.sort((a, b) => (a.orden || 99) - (b.orden || 99));

        // Ordenar también los submenús por orden
        this.dynamicMenuItems.forEach(menuItem => {
          if (menuItem.subMenu && menuItem.subMenu.length > 0) {
            menuItem.subMenu.sort((a, b) => (a.orden || 1) - (b.orden || 1));
          }
        });
      } catch (error) {
        this.error = 'Error al cargar el menú';
        this.loadStaticMenu();
      } finally {
        this.loading = false;
      }
    },

    loadStaticMenu() {
      // Menú estático como fallback
      this.dynamicMenuItems = [
        {
          title: "Catálogo",
          icon: "store",
          subMenu: [
            {
              title: "Gestión de Tiendas",
              route: "gestion-tiendas",
              icon: "store-alt",
            },
          ],
        },
        {
          title: "Administración",
          icon: "shield-alt",
          subMenu: [
            {
              title: "Usuario Rol",
              route: "catalogos-usuario-rol",
              icon: "users",
            },
          ],
        },
      ];
    },

    loadUserPreferences() {
      // Cargar estado del menú desde sessionStorage
      if (window.innerWidth <= 1100) {
        const savedState = sessionStorage.getItem("mobileMenuOpen");
        if (savedState !== null) {
          this.isMobileMenuOpen = JSON.parse(savedState);
        }
      }

      // Cargar modo oscuro desde sessionStorage
      const savedDarkMode = sessionStorage.getItem("darkMode");
      if (savedDarkMode) {
        this.isDarkMode = savedDarkMode === "true";
        if (this.isDarkMode) {
          document.body.classList.add("dark");
          document.body.classList.remove("light-theme");
          document.body.classList.add("dark-theme");
        } else {
          document.body.classList.remove("dark");
          document.body.classList.remove("dark-theme");
          document.body.classList.add("light-theme");
        }
      }
    },

    getFilteredSubMenu(menuItem) {
      if (!menuItem.subMenu) return [];

      // Devolver todos los submenús sin filtros
      return menuItem.subMenu || [];
    },

    // Toggle dropdown for catalog menu
    toggleDropdown(title) {
      this.openDropdown = this.openDropdown === title ? null : title;
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      sessionStorage.setItem("darkMode", this.isDarkMode);
      if (this.isDarkMode) {
        document.body.classList.add("dark");
        document.body.classList.remove("light-theme");
        document.body.classList.add("dark-theme");
      } else {
        document.body.classList.remove("dark");
        document.body.classList.remove("dark-theme");
        document.body.classList.add("light-theme");
      }
    },

    toggleCollapse() {
      if (!this.isMobileMenuOpen && !this.isOverlayMode()) {
        this.isCollapsed = !this.isCollapsed;
      }
    },

    CerrarLogin() {
      this.$emit("logout");
    },

    navigateTo(routeName) {
      try {
        if (this.$router && routeName) {
          this.$router.push({ name: routeName }).catch(err => {
          });
        }
      } catch (error) {
      }
    },

    toggleMobileMenu() {
      if (!this.isOverlayMode()) {
        this.isCollapsed = !this.isCollapsed;
        return;
      }

      this.isMobileMenuOpen = !this.isMobileMenuOpen;

      if (this.isMobileMenuOpen) {
        this.$nextTick(() => {
          this.$refs.menuContainer.focus();

          if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
          }

          if (this.clickHandlerActive) {
            document.removeEventListener("click", this.handleOutsideClick);
            this.clickHandlerActive = false;
          }

          this.clickTimeout = setTimeout(() => {
            document.addEventListener("click", this.handleOutsideClick);
            this.clickHandlerActive = true;
          }, 300);
        });
      } else {
        this.cleanupClickHandler();
      }

      if (window.innerWidth <= 1100) {
        sessionStorage.setItem("mobileMenuOpen", this.isMobileMenuOpen);
      }
    },

    cleanupClickHandler() {
      if (this.clickTimeout) {
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
      }

      if (this.clickHandlerActive) {
        document.removeEventListener("click", this.handleOutsideClick);
        this.clickHandlerActive = false;
      }
    },

    closeMobileMenu() {
      if (this.isMobileMenuOpen) {
        this.isMobileMenuOpen = false;
        this.cleanupClickHandler();

        if (window.innerWidth <= 1100) {
          sessionStorage.setItem("mobileMenuOpen", false);
        }
      }
    },

    handleOutsideClick(event) {
      if (!this.clickHandlerActive) return;

      const menuContainer = this.$refs.menuContainer;
      const menuToggle = document.querySelector(".mobile-menu-toggle");

      if (
        menuContainer &&
        !menuContainer.contains(event.target) &&
        menuToggle &&
        !menuToggle.contains(event.target)
      ) {
        this.clickHandlerActive = false;
        this.closeMobileMenu();
      }
    },

    handleMenuBlur(event) {
      if (window.innerWidth <= 1100) {
        if (!this.$refs.menuContainer.contains(event.relatedTarget)) {
          setTimeout(() => {
            this.closeMobileMenu();
          }, 100);
        }
      }
    },

    handleMouseOver() {
      if (!this.isOverlayMode() && this.isCollapsed && !this.isHovering && !this.isMobileMenuOpen) {
        this.isHovering = true;
        this.isCollapsed = false;
      }
    },

    handleMouseLeave() {
      if (!this.isOverlayMode() && this.isHovering && !this.isMobileMenuOpen) {
        this.isHovering = false;
        this.isCollapsed = true;
      }
    },
  },

  mounted() {
    this.resizeHandler = () => {
      this.syncMenuMode();
    };

    window.addEventListener("resize", this.resizeHandler);
  },

  beforeUnmount() {
    this.cleanupClickHandler();
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
  },
};
</script>

<style scoped>
.menu-container {
  /* Variables específicas del menú */
  --menu-hover-background-color: rgba(116, 168, 11, 0.1);
  --menu-hover-text-color: var(--primary-color);
  --submenu-background-color: var(--card-background-color);
  --submenu-hover-background-color: rgba(116, 168, 11, 0.15);
  --card-box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  --overlay-background-color: rgba(0, 0, 0, 0.5);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  display: flex;
  flex-direction: column;
  background-color: var(--background-color);
  height: 100vh;
  width: 300px;
  color: var(--text-color);
  transition: width 0.3s, padding 0.3s;
  -webkit-transition: width 0.3s, padding 0.3s;
  box-shadow: var(--card-box-shadow);
  z-index: 1000;
  white-space: nowrap;
  padding: 10px;
  overflow: hidden;
  font-size: 0.95rem;
}

.logo-section {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 150px;
  flex-shrink: 0;
  width: 100%;
  padding: 0;
  margin: 0;
  overflow: hidden;
}

.logo-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
  -webkit-transition: all 0.3s ease;
}

.logo {
  max-width: 80%;
  max-height: 160px;
  object-fit: contain;
  transition: all 0.3s ease;
  -webkit-transition: all 0.3s ease;
}

.logo-collapsed {
  max-width: 80%;
  max-height: 150px;
  object-fit: contain;
}

.menu-section {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 0;
  width: 100%;
  flex: 1;
  min-height: 0;
  position: relative;
}

.user-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  padding: 20px 0;
  align-items: center;
  flex-shrink: 0;
}

.collapsed {
  width: 70px;
}

.menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
}

.menu-item {
  cursor: pointer;
  font-weight: bold;
  color: inherit;
  padding: 0;
  border-radius: 8px;
  transition: background-color 0.3s, color 0.3s;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  width: 100%;
  z-index: 100;
  margin-bottom: 5px;
}

.menu-item>div {
  cursor: default;
  position: relative;
  z-index: 1;
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.menu-item>div.clickable {
  cursor: pointer;
}


.dropdown-icon {
  position: absolute;
  right: 15px;
  font-size: 0.8rem;
  transition: transform 0.3s;
}

.menu-item span,
.sub-menu-item span {
  margin-left: 10px;
  overflow: hidden;
  display: inline-block;
}

.sub-menu-container {
  position: relative;
  margin-top: 5px;
  margin-left: 20px;
  width: calc(100% - 20px);
  background-color: var(--submenu-background-color);
  border-radius: 6px;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.sub-menu-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sub-menu-item {
  cursor: pointer;
  color: var(--text-color);
  padding: 8px 15px;
  border-radius: 4px;
  margin: 2px 8px;
  transition: background-color 0.3s, color 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  font-size: 0.9rem;
}

.menu-container li.sub-menu-item:hover {
  color: var(--primary-color) !important;
  background-color: transparent !important;
}

.menu-container li.sub-menu-item:hover span {
  color: var(--primary-color) !important;
}

.menu-container li.sub-menu-item:hover .fixed-icon-size {
  color: var(--primary-color) !important;
}

.dark-mode .menu-container li.sub-menu-item:hover {
  color: var(--primary-color) !important;
  background-color: transparent !important;
}

.dark-mode .menu-container li.sub-menu-item:hover span {
  color: var(--primary-color) !important;
}

.dark-mode .menu-container li.sub-menu-item:hover .fixed-icon-size {
  color: var(--primary-color) !important;
}

.submenu-item-text {
  font-weight: 400;
  transition: color 0.3s;
}

.user-text {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px;
  background-color: var(--submenu-background-color);
  border-radius: 8px;
  margin-bottom: 10px;
}

.user-photo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid var(--border-color);
}

.user-name {
  font-size: 0.9rem;
  font-weight: 500;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.logout-button {
  background-color: var(--danger-color);
  color: var(--color-white);
  border: none;
  padding: 10px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.3s;
  min-width: 44px; /* Mejorado accesibilidad: mínimo 44px para tap targets */
  min-height: 44px; /* Mejorado accesibilidad: mínimo 44px para tap targets */
}

.logout-button:hover {
  background-color: var(--danger-color-hover);
}

.switchdiv {
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--switch-background-color);
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked+.slider {
  background-color: var(--primary-color);
}

input:checked+.slider:before {
  transform: translateX(26px);
}

.fixed-icon-size {
  font-size: 1rem;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-menu-toggle {
  display: none;
  position: fixed;
  top: 15px;
  left: 15px;
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  box-shadow: var(--card-box-shadow);
  z-index: 1001;
}

.mobile-menu-toggle.desktop-visible {
  display: block;
}

.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: none;
}

/* Dark mode styles */
.dark-mode .menu-container {
  background-color: var(--background-color);
  color: var(--text-color);
}


.dark-mode .sub-menu-container { background-color: var(--card-background-color); }

.dark-mode .sub-menu-item:hover {
  background-color: rgba(116, 168, 11, 0.25);
}

.dark-mode .user-text { background-color: var(--card-background-color); }


@media (max-width: 1100px) {
  .mobile-menu-toggle {
    display: block;
  }

  .menu-container {
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 300px;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 10px;
  }

  .menu-container.mobile-open {
    transform: translateX(0);
    width: 270px;
    max-height: 100vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .menu-overlay {
    display: block;
  }

  .sub-menu-item {
    padding: 8px 15px;
    min-height: 36px;
    margin: 2px 8px;
  }

}

@media (min-width: 1101px) {
  .menu-container.desktop-hidden {
    width: 0;
    min-width: 0;
    padding: 0;
    border: 0;
    box-shadow: none;
    overflow: hidden;
  }

  .menu-container.desktop-hidden * {
    pointer-events: none;
  }

  .mobile-menu-toggle {
    display: none;
  }

  .mobile-menu-toggle.desktop-visible {
    display: block;
  }
}
</style>
